import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { asUserId } from '@leanin/shared/ids';
import {
  gateOnboardingComplete,
  memberPhotoUrl,
  sessionQuery,
  useSession,
} from '@/lib/use-session';
import { myPinsQuery, useMyPins } from '@/lib/queries/pins';
import { Shell } from '@/components/shell/Shell';
import { SessionErrorPage } from '@/components/shell/SessionErrorPage';
import { ViewerProvider, type ViewerIdentity } from '@/lib/viewer-context';
import { viewerWithAvatarSrc } from '@/lib/viewer';

// Auth gate + onboarding gate + app shell. Runs before any nested authed
// route renders. Pre-warms session + pins via TanStack Query so child
// components reading `useSession()` / `useMyPins()` get a cache hit on
// first render. Unauthenticated visitors are bounced to `/auth/login`;
// authenticated-but-not-onboarded visitors are bounced to
// `/onboarding/profile`.
//
// Why the onboarding gate lives HERE: `onboardingCompletedAt === null`
// is the single signal that a user hasn't finished the persona tour
// (the leader-grant + member-provision both completed during signup,
// but the recipient never got the leader-tour). Without this gate, a
// new invitee who closes the tab after `/auth/finishing` and re-opens
// directly at `/feed` or `/networks/{id}` lands there with the role
// but never sees their tour — the only previous code path that caught
// this was LoginForm's mid-onboarding re-route, which fires only when
// they manually visit `/auth/login`.
//
// The onboarding routes live OUTSIDE `_authed` (at `/onboarding/*`), so
// this gate won't create a loop with them — they handle their own auth
// check. Auth pages (`/auth/*`) likewise aren't under `_authed`.
//
// Error classification: the session probe resolving NULL is the
// DEFINITIVE signed-out verdict (the server answered a bare 401/404) →
// redirect to /auth/login. The probe THROWING (network failure, 5xx,
// Clerk outage — react-query has already exhausted its retries) means
// the auth state is UNKNOWN → let the error propagate so the route's
// `errorComponent` renders a retryable surface. Classifying "couldn't
// determine" as "signed out" bounced genuinely signed-in users to the
// login page whenever the first probe failed.
export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionQuery);
    if (ctx === null) {
      throw redirect({ to: '/auth/login', search: { from: location.href } });
    }
    // Onboarding gate — shared with `_public` so the same rule covers
    // signed-in viewers on auth-optional routes (e.g. `/networks/{id}`
    // is under `_public` because anonymous visitors can browse). See
    // `gateOnboardingComplete` for the rule definition.
    gateOnboardingComplete(ctx);
    // Pin prefetch — non-blocking; shell handles loading state.
    context.queryClient.prefetchQuery(myPinsQuery);
    return { session: ctx.session, member: ctx.member };
  },
  errorComponent: SessionErrorPage,
  component: AuthedShell,
});

function AuthedShell() {
  const { data: ctx } = useSession();
  const { data: pins } = useMyPins();
  if (!ctx) return null;
  const { member } = ctx;
  const photoUrl = memberPhotoUrl(member) ?? null;
  const viewer: ViewerIdentity = {
    id: asUserId(member.id),
    displayName: `${member.firstName} ${member.lastName}`.trim(),
    photoUrl,
  };
  const shellUser = {
    ...viewerWithAvatarSrc({
      id: member.id,
      firstName: member.firstName,
      lastName: member.lastName,
      photoUrl: photoUrl ?? undefined,
    }),
    platformRole: member.platformRole,
  };
  return (
    <ViewerProvider value={viewer}>
      <Shell user={shellUser} pins={pins ?? []}>
        <Outlet />
      </Shell>
    </ViewerProvider>
  );
}
